import type { SignupStep } from './steps';

/**
 * The funnel's step-ordering logic. All pure — the flow provider is a thin
 * stateful shell over these. Only the consolidated sequence is supported
 * (the legacy route maps / cookie / `?signupFlow=` override are not ported).
 */
export type FlowSequenceContext = {
  isStartup: boolean;
  isGoogleSignup: boolean;
  showPlanChoice: boolean;
  isBuilder: boolean;
};

export function buildRouteSequence({
  isStartup,
  isGoogleSignup,
  showPlanChoice,
  isBuilder,
}: FlowSequenceContext): SignupStep[] {
  const steps: SignupStep[] = ['index'];
  if (!isGoogleSignup) {
    steps.push('confirmation', 'password');
  }
  if (!isStartup) {
    steps.push('company');
  }
  steps.push('onboarding_usage');
  if (!isBuilder) {
    steps.push('channels');
    if (showPlanChoice) {
      steps.push('plan_choice');
    }
  }
  if (isStartup) {
    steps.push('startup_complete');
  }
  return steps;
}

/**
 * The next step after `current`, or `'complete'` when the sequence is
 * exhausted. `null` when `current` isn't in the sequence — the guards bounce
 * those to the entry step.
 */
export function getNextStep(current: SignupStep, context: FlowSequenceContext): SignupStep | 'complete' | null {
  const sequence = buildRouteSequence(context);
  const index = sequence.indexOf(current);
  if (index < 0) {
    return null;
  }
  return sequence[index + 1] ?? 'complete';
}

/** Use cases that qualify for the Builder plan-choice step. */
const BUILDER_PLAN_CHOICE_USE_CASES = new Set(['transactional', 'newsletters']);

export function shouldShowPlanChoice({
  hasPromoCode,
  messagingUseCases,
}: {
  hasPromoCode: boolean;
  messagingUseCases: string[];
}): boolean {
  return (
    !hasPromoCode &&
    messagingUseCases.length > 0 &&
    messagingUseCases.every((useCase) => BUILDER_PLAN_CHOICE_USE_CASES.has(useCase))
  );
}

export type ResolvedSignupType = 'startup' | 'builder' | 'journeys';

export function resolveSignupType({
  isStartup,
  isBuilder,
  showPlanChoice,
  selectedPlan,
}: {
  isStartup: boolean;
  isBuilder: boolean;
  showPlanChoice: boolean;
  selectedPlan: 'essentials' | 'builders' | null;
}): ResolvedSignupType {
  if (isStartup) {
    return 'startup';
  }
  if (isBuilder) {
    return 'builder';
  }
  return showPlanChoice && selectedPlan === 'builders' ? 'builder' : 'journeys';
}

export function recommendedPlan(showPlanChoice: boolean): 'builders' | 'essentials' {
  return showPlanChoice ? 'builders' : 'essentials';
}

/**
 * The `resolved_signup_type` value step analytics carry. Before the visitor
 * has picked a plan on a plan-choice-eligible path, the resolution is unknown —
 * report `undefined` rather than a guess. Builder short-circuits before the
 * pending-plan check: builders never see the plan-choice step, so their type
 * is already resolved even when qualifying use cases make `showPlanChoice`
 * true while `selectedPlan` is unset.
 */
export function resolveSignupTypeForTracking(args: {
  isStartup: boolean;
  isBuilder: boolean;
  showPlanChoice: boolean;
  selectedPlan: 'essentials' | 'builders' | null;
}): ResolvedSignupType | undefined {
  if (args.isStartup) {
    return 'startup';
  }
  if (args.isBuilder) {
    return 'builder';
  }
  if (args.showPlanChoice && args.selectedPlan == null) {
    return undefined;
  }
  return resolveSignupType(args);
}
